import './AnalyticsCustomerPopup.css';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import React, { useEffect, useState } from 'react';
import { CloseAnalyticsCustomerPopupIcon } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCustomerPopup/CloseAnalyticsCustomerPopupIcon';
import { DateItems } from 'src/pages/AnalyticsPage/AnalyticsCustomer/DateItems';
import { useParams } from 'react-router-dom';
import { useLazyGetCoCustomerTimeSeriesQuery } from 'src/services';
import { AnalyticsCustomerLineChartData } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCustomerLineChart/AnalyticsCustomerLineChart';
import { AnalyticsCustomerBarChartData } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCustomerBarChart/AnalyticsCustomerBarChart';
import { useLoader } from 'src/provider/LoaderProvider';
import { useAppSelector } from 'src/store';
import { SelectDateXAxis } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCustomerPopup/SelectDateXAxis';
import { SelectDateYAxis } from 'src/pages/AnalyticsPage/AnalyticsCustomer/AnalyticsCustomerPopup/SelectDateYAxis';
import { EscalationTrendsLineChart } from 'src/pages/AnalyticsPage/AnalyticsCustomer/EscalationTrendsCard/EscalationTrendsLineChart';

const ChartIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="18"
    height="18"
    viewBox="0 0 20 20"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3.333 16.667V3.333M3.333 16.667h13.334M6.667 13.333l3.333-3.333 2.5 2.5 3.75-4.583"
      stroke="#2A2A3A"
      strokeWidth="1.667"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AnalyticsCustomerPopup = ({
  disabled = false,
  popupTitle = '',
  title = '',
  stackTitle = '',
  dataKey = '',
  dataKeyStack = '',
  className = '',
  dateRange,
  chartType,
  chartHeight = null,
  color = null,
  xAxisSelect = [],
  yAxisSelect = [],
  cartesianGridVisible = false,
}) => {
  const defaultDateItem = DateItems.find((item) => item.default) || DateItems[0];
  const [open, setOpen] = useState(false);
  const { id } = useParams();
  const loader = useLoader();
  const { start_time, end_time, group_by, timezone, dateX } = useAppSelector(
    (s) => s.analyticSlices,
  );
  const [timeSeriesRequest, setTimeSeriesRequest] = useState({
    project_ids: [id],
    start_time: start_time || defaultDateItem.getStartTime(),
    end_time: end_time || defaultDateItem.getEndTime(),
    group_by: group_by || defaultDateItem.groupBy,
    tz_offset: timezone.offset * 60,
  });
  const [timeSeriesResponse, setTimeSeriesResponse] = useState([]);
  const [fetchTimeSeriesQuery, { data: timeSeriesData, isFetching, isError, error }] =
    useLazyGetCoCustomerTimeSeriesQuery() as any;

  useEffect(() => {
    if (isFetching) {
      loader.show();
    } else {
      loader.hide();
    }
  }, [isFetching]);

  useEffect(() => {
    if (isError) {
      error.status === 404 && setTimeSeriesResponse([]);
    }
  }, [isError]);

  useEffect(() => {
    if (!dateRange) {
      return;
    }
    const isCustom = dateRange.label === 'Custom';
    const dateItem = DateItems.find((item) => item.label === dateRange.label);

    setTimeSeriesRequest({
      ...timeSeriesRequest,
      start_time: start_time || (isCustom ? dateRange.start_time : dateItem.getStartTime()),
      end_time: end_time || (isCustom ? dateRange.end_time : dateItem.getEndTime()),
      group_by: dateX ? dateX : isCustom ? group_by : dateItem.groupBy,
      tz_offset: timezone.offset * 60,
    });
  }, [dateRange, timezone, dateX, end_time, start_time]);

  useEffect(() => {
    if (open && timeSeriesRequest) {
      fetchTimeSeriesQuery(timeSeriesRequest);
    }
  }, [open, timeSeriesRequest]);

  useEffect(() => {
    if (timeSeriesData && timeSeriesData.length) {
      setTimeSeriesResponse(timeSeriesData);
    }
  }, [timeSeriesData]);

  return (
    <Dialog onOpenChange={setOpen} open={open}>
      <DialogTrigger
        disabled={disabled}
        asChild
        className={clsx(
          disabled && 'pointer-events-none opacity-50',
          'cursor-pointer analytic-popup-icon',
        )}
      >
        <button
          type="button"
          aria-label="Open chart"
          className="flex items-center justify-center p-2 rounded-sm-5 border border-[#DFDFE8] bg-white drop-shadow-[0px_2px_1px_rgba(0,0,0,0.25)] disabled:cursor-default"
        >
          <ChartIcon />
        </button>
      </DialogTrigger>
      <DialogContent closeButtonHidden={true} className={clsx(className, '!max-w-[90vw]')}>
        <DialogHeader className={'flex-row items-center justify-between'}>
          <DialogTitle className="text-start">
            <div className={'flex flex-row gap-[8px] items-center justify-center'}>
              <span className="font-jamjuree font-bold text-xl">{popupTitle || title}</span>
            </div>
          </DialogTitle>
          <div className="flex items-center gap-4">
            {Boolean(yAxisSelect?.length) && (
              <div className="flex items-center gap-4">
                <span className="text-xs opacity-30 font-jamjuree">Time range (y)</span>
                <SelectDateYAxis selectOptions={yAxisSelect} />
              </div>
            )}
            {Boolean(xAxisSelect?.length) && (
              <div className="flex items-center gap-4">
                <span className="text-xs opacity-30 font-jamjuree">Date range (x)</span>
                <SelectDateXAxis selectOptions={xAxisSelect} />
              </div>
            )}
            <DialogClose
              className={
                '!m-0 rounded-sm-5 bg-[#fff] p-[7px] analytics-popup-close-icon shadow-[0_2px_2px_0_rgba(0,0,0,0.25)]'
              }
            >
              <CloseAnalyticsCustomerPopupIcon />
            </DialogClose>
          </div>
        </DialogHeader>
        <DialogFooter className="max-md:flex-row max-md:gap-2.5 bg-white rounded-sm-5">
          {timeSeriesResponse.length ? (
            <div className={'w-full h-full border border-[#DFDFE8] rounded-[7px]'}>
              {chartType === 'lineChart' && (
                <AnalyticsCustomerLineChartData
                  title={title}
                  data={timeSeriesResponse}
                  dataKey={dataKey}
                  groupBy={timeSeriesRequest.group_by}
                  color={color}
                />
              )}
              {chartType === 'barChart' && (
                <AnalyticsCustomerBarChartData
                  title={title}
                  stackTitle={stackTitle}
                  data={timeSeriesResponse}
                  dataKey={dataKey}
                  dataKeyStack={dataKeyStack}
                  groupBy={timeSeriesRequest.group_by}
                />
              )}
              {chartType === 'escalationTrends' && (
                <EscalationTrendsLineChart
                  data={timeSeriesResponse}
                  height={chartHeight}
                  cartesianGridVisible={cartesianGridVisible}
                />
              )}
            </div>
          ) : (
            <div className={'w-full h-[50vh] text-sm flex items-center justify-center font-medium'}>
              No data for the selected time range
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
